import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { resolveUserContext } from '../lib/userContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { Trophy as A1, Users as A2, Calendar as A3, Award as A4, Search as A5, Sparkles as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, Filter, DollarSign, GraduationCap, ExternalLink,
  BookmarkPlus, BookmarkCheck, X, ChevronDown, Star, TrendingUp,
  MapPin, Award, Trophy, Users, Globe, Bookmark, Sparkles,
  CheckCircle, AlertCircle, Monitor, Building2, Wifi, Calendar,
  Target, Rocket, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const CATEGORIES = [
  { key: '', label: 'All Categories', icon: Award },
  { key: 'academic_competition', label: 'Academic Competitions', icon: Trophy },
  { key: 'pre_college', label: 'Pre-College Programs', icon: GraduationCap },
  { key: 'honor_society', label: 'Honor Societies', icon: Star },
  { key: 'leadership', label: 'Leadership Programs', icon: Users },
  { key: 'summer_program', label: 'Summer Programs', icon: Calendar },
];

// College students get a college-relevant taxonomy, no Pre-College / HS-only categories.
const COLLEGE_CATEGORIES = [
  { key: '', label: 'All Categories', icon: Award },
  { key: 'fellowship', label: 'Fellowships', icon: Award },
  { key: 'research_program', label: 'Undergraduate Research', icon: Sparkles },
  { key: 'career_competition', label: 'Case Comps & Hackathons', icon: Trophy },
  { key: 'leadership', label: 'Leadership Programs', icon: Users },
  { key: 'summer_program', label: 'Summer Programs', icon: Calendar },
  { key: 'accelerator', label: 'Accelerators', icon: Rocket },
];

// Union used only for card icon/label lookups.
const ALL_CATS = [...CATEGORIES, ...COLLEGE_CATEGORIES.filter(c => !CATEGORIES.some(x => x.key === c.key))];

const INTEREST_TAGS = [
  { key: '', label: 'All Interests' },
  { key: 'STEM', label: 'STEM' }, { key: 'Mathematics', label: 'Math' },
  { key: 'Science', label: 'Science' }, { key: 'Computer Science', label: 'Computer Science' },
  { key: 'Engineering', label: 'Engineering' }, { key: 'Business', label: 'Business' },
  { key: 'Leadership', label: 'Leadership' }, { key: 'Arts', label: 'Arts' },
  { key: 'Writing', label: 'Writing' }, { key: 'Music', label: 'Music' },
  { key: 'Government', label: 'Government' }, { key: 'Community Service', label: 'Community Service' },
];

const SORT_OPTIONS = [
  { key: 'relevance', label: 'Best Match' },
  { key: 'deadline', label: 'Soonest Deadline' },
  { key: 'cost_low', label: 'Lowest Cost' },
  { key: 'newest', label: 'Newest' },
];

const TRACK_STATUSES = [
  { key: 'saved', label: 'Saved', color: 'slate' },
  { key: 'interested', label: 'Interested', color: 'amber' },
  { key: 'applying', label: 'Applying', color: 'blue' },
  { key: 'accepted', label: 'Accepted', color: 'emerald' },
];

function MatchBadge({ score }) {
  const color = score >= 70 ? 'emerald' : score >= 40 ? 'amber' : 'slate';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-500/20 text-${color}-400`}>
      <Star className="w-3 h-3" /> {score}%
    </span>
  );
}

function LocationBadge({ program }) {
  const loc = program.location_type || 'in_person';
  if (loc === 'virtual') return <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-400 rounded-md text-xs"><Wifi className="w-3 h-3" /> Virtual</span>;
  if (loc === 'hybrid') return <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-md text-xs"><Monitor className="w-3 h-3" /> Hybrid</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-500/10 text-slate-300 rounded-md text-xs"><Building2 className="w-3 h-3" /> In-Person</span>;
}

function ProgramCard({ prog, savedMap, onSave, onUnsave, onSelect }) {
  const isSaved = savedMap[prog.id];
  const match = prog.match || {};
  const catMeta = ALL_CATS.find(c => c.key === prog.category) || ALL_CATS[0];
  const CatIcon = catMeta.icon || Award;
  const cost = prog.free_flag ? 'Free' : prog.cost_max ? `$${prog.cost_max.toLocaleString()}` : 'Varies';

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5 hover:border-blue-500/30 transition-all group cursor-pointer"
      onClick={() => onSelect(prog)} data-testid={`program-card-${prog.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-base group-hover:text-blue-400 transition-colors truncate">{prog.program_name}</h3>
          <p className="text-slate-300 text-xs mt-0.5 truncate">{prog.organization_name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {match.overall_score > 0 && <MatchBadge score={match.overall_score} />}
          <button onClick={(e) => { e.stopPropagation(); isSaved ? onUnsave(prog.id) : onSave(prog.id); }}
            className={`p-1.5 rounded-lg transition-colors ${isSaved ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-800 text-slate-300 hover:text-blue-400'}`}
            data-testid={`save-prog-${prog.id}`}>
            {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <BookmarkPlus className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <p className="text-slate-300 text-sm line-clamp-2 mb-3">{prog.description}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 rounded-md text-xs">
          <CatIcon className="w-3 h-3" /> {catMeta.label || prog.category}
        </span>
        <LocationBadge program={prog} />
        {prog.national_flag && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-md text-xs"><Globe className="w-3 h-3" /> National</span>}
        <span className={`px-2 py-1 rounded-md text-xs ${prog.free_flag ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-300'}`}>
          <DollarSign className="w-3 h-3 inline" /> {cost}
        </span>
        {prog.stem_flag && <span className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-md text-xs">STEM</span>}
        {prog.arts_flag && <span className="px-2 py-1 bg-pink-500/10 text-pink-400 rounded-md text-xs">Arts</span>}
        {prog.selective_flag && <span className="px-2 py-1 bg-amber-500/10 text-amber-400 rounded-md text-xs">Selective</span>}
      </div>
      {match.match_reasons?.length > 0 && (
        <div className="flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-amber-400/80 text-xs">{match.match_reasons[0]}</p>
        </div>
      )}
    </motion.div>
  );
}

function ProgramDetailModal({ prog, savedMap, onClose, onSave, onUnsave, onTrack }) {
  if (!prog) return null;
  const match = prog.match || {};
  const isSaved = savedMap[prog.id];
  const cost = prog.free_flag ? 'Free' : (prog.cost_min && prog.cost_max) ? `$${prog.cost_min.toLocaleString()} - $${prog.cost_max.toLocaleString()}` : prog.cost_max ? `Up to $${prog.cost_max.toLocaleString()}` : 'Varies';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="program-detail-modal">
        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-purple-600/15 to-blue-600/10 border-b border-slate-800">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">{prog.program_name}</h2>
              <p className="text-slate-300 text-sm mt-1">{prog.organization_name}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {match.overall_score > 0 && <MatchBadge score={match.overall_score} />}
                <LocationBadge program={prog} />
                <span className={`text-sm font-medium ${prog.free_flag ? 'text-emerald-400' : 'text-slate-300'}`}>{cost}</span>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:text-white shrink-0"><X className="w-5 h-5" /></Button>
          </div>
        </div>
        {/* Body */}
        <div className="p-5 overflow-y-auto max-h-[calc(85vh-200px)] space-y-5">
          <p className="text-slate-300 leading-relaxed">{prog.description}</p>
          {match.match_reasons?.length > 0 && (
            <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
              <h4 className="text-amber-400 text-sm font-semibold flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4" /> Why This Matches You</h4>
              <ul className="space-y-1">
                {match.match_reasons.map((r, i) => <li key={i} className="text-amber-300/80 text-sm flex items-start gap-2"><CheckCircle className="w-3 h-3 mt-0.5 shrink-0" /> {r}</li>)}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-800/40 rounded-lg"><p className="text-slate-300 text-xs mb-1">Cost</p><p className={`font-bold ${prog.free_flag ? 'text-emerald-400' : 'text-white'}`}>{cost}</p>{prog.financial_aid_available && <p className="text-emerald-400 text-xs mt-1">Financial aid available</p>}</div>
            <div className="p-3 bg-slate-800/40 rounded-lg"><p className="text-slate-300 text-xs mb-1">Deadline</p><p className="text-white text-sm">{prog.deadline_date || 'See website'}</p><p className="text-slate-300 text-[10px]">{prog.deadline_type === 'annual_recurring' ? 'Recurring annually' : prog.deadline_type || ''}</p></div>
            <div className="p-3 bg-slate-800/40 rounded-lg"><p className="text-slate-300 text-xs mb-1">Location</p><p className="text-white text-sm capitalize">{prog.location_type?.replace('_', ' ') || 'In-Person'}</p>{prog.national_flag && <p className="text-emerald-400 text-xs mt-0.5">Open nationally</p>}{!prog.national_flag && prog.eligible_states?.length > 0 && <p className="text-purple-400 text-xs mt-0.5">{prog.eligible_states.join(', ')}</p>}</div>
            <div className="p-3 bg-slate-800/40 rounded-lg"><p className="text-slate-300 text-xs mb-1">Season</p><p className="text-white text-sm capitalize">{prog.season || 'Year-round'}</p></div>
          </div>
          {prog.grade_levels?.length > 0 && (
            <div><p className="text-slate-300 text-xs font-semibold mb-2">Eligible Grade Levels</p>
              <div className="flex flex-wrap gap-1.5">{prog.grade_levels.map((g, i) => <span key={i} className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-xs capitalize">{g.replace('_', ' ')}</span>)}</div></div>
          )}
          {prog.subject_tags?.length > 0 && (
            <div><p className="text-slate-300 text-xs font-semibold mb-2">Subjects & Topics</p>
              <div className="flex flex-wrap gap-1.5">{prog.subject_tags.map((s, i) => <span key={i} className="px-2 py-1 bg-blue-500/10 text-blue-300 rounded text-xs">{s}</span>)}</div></div>
          )}
          {isSaved && (
            <div><p className="text-slate-300 text-xs font-semibold mb-2">Application Tracker</p>
              <div className="flex gap-2 flex-wrap">
                {TRACK_STATUSES.map(ts => (
                  <button key={ts.key} onClick={() => onTrack(prog.id, ts.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isSaved.status === ts.key ? `bg-${ts.color}-500/30 text-${ts.color}-300 border border-${ts.color}-500/50` : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                    data-testid={`track-prog-${ts.key}`}>{ts.label}</button>
                ))}
              </div></div>
          )}
          <div className="flex items-start gap-2 p-3 bg-slate-800/30 rounded-lg">
            <AlertCircle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
            <p className="text-[11px] text-slate-300">Program details may change. Always verify current information directly with the program provider.</p>
          </div>
        </div>
        <div className="p-4 border-t border-slate-800 flex gap-3">
          <Button onClick={() => { isSaved ? onUnsave(prog.id) : onSave(prog.id); }}
            className={isSaved ? 'bg-blue-600 hover:bg-blue-700 flex-1' : 'bg-slate-700 hover:bg-slate-600 flex-1'} data-testid="detail-save-prog">
            {isSaved ? <><BookmarkCheck className="w-4 h-4 mr-2" /> Saved</> : <><BookmarkPlus className="w-4 h-4 mr-2" /> Save Program</>}
          </Button>
          {prog.website_url && <a href={prog.website_url} target="_blank" rel="noopener noreferrer" className="flex-1"><Button className="btn-primary w-full" data-testid="learn-more-btn"><ExternalLink className="w-4 h-4 mr-2" /> Learn More</Button></a>}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function ProgramsPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader, user } = useAuth();
  const ctx = resolveUserContext(user);
  const visibleCats = ctx.isCollege ? COLLEGE_CATEGORIES : CATEGORIES;
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [savedMap, setSavedMap] = useState({});
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(ctx.isCollege ? 'recommended' : 'search');
  const [savedList, setSavedList] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterLocType, setFilterLocType] = useState('');
  const [filterInterest, setFilterInterest] = useState('');
  const [filterFreeOnly, setFilterFreeOnly] = useState(false);
  const [filterGpa, setFilterGpa] = useState('');
  const [sortBy, setSortBy] = useState('relevance');

  const headers = getAuthHeader();

  useEffect(() => {
    const init = async () => {
      try {
        const [statsRes, savedRes] = await Promise.all([
          axios.get(`${API}/programs/v2/stats`, headers),
          axios.get(`${API}/programs/v2/saved`, headers),
        ]);
        setStats(statsRes.data);
        const map = {};
        (savedRes.data.programs || []).forEach(p => { map[p.id] = { status: p.tracking?.status || 'saved' }; });
        setSavedMap(map);
        setSavedList(savedRes.data.programs || []);
      } catch {}
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPrograms = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (filterCategory) params.append('category', filterCategory);
      if (filterState) params.append('state', filterState);
      if (filterLocType) params.append('location_type', filterLocType);
      if (filterInterest) params.append('interest', filterInterest);
      if (filterFreeOnly) params.append('cost_free', 'true');
      if (filterGpa) params.append('gpa_min', filterGpa);
      params.append('sort_by', sortBy);
      params.append('page', pg);
      params.append('limit', 20);
      const res = await axios.get(`${API}/programs/v2/search?${params}`, headers);
      setPrograms(res.data.programs || []);
      setTotalResults(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
      setPage(pg);
    } catch { toast.error('Failed to load programs'); }
    finally { setLoading(false); }
  }, [searchQuery, filterCategory, filterState, filterLocType, filterInterest, filterFreeOnly, filterGpa, sortBy, headers]);

  const fetchRecs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/programs/v2/recommendations/personalized?limit=20`, headers);
      setPrograms(res.data.recommendations || []);
      setTotalResults(res.data.total_matches || 0);
      setTotalPages(1);
    } catch { toast.error('Failed to load recommendations'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => {
    if (activeTab === 'search') fetchPrograms(1);
    else if (activeTab === 'recommended') fetchRecs();
    else if (activeTab === 'saved') {
      axios.get(`${API}/programs/v2/saved`, headers).then(res => setSavedList(res.data.programs || [])).catch(() => {});
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (id) => { try { await axios.post(`${API}/programs/v2/save/${id}`, {}, headers); setSavedMap(prev => ({ ...prev, [id]: { status: 'saved' } })); celebrate('Saved. That is the kind of line that makes a record memorable.'); } catch { toast.error('Failed'); } };
  const handleUnsave = async (id) => { try { await axios.delete(`${API}/programs/v2/save/${id}`, headers); setSavedMap(prev => { const n = { ...prev }; delete n[id]; return n; }); toast.success('Removed'); } catch { toast.error('Failed'); } };
  const handleTrack = async (id, status) => { try { await axios.put(`${API}/programs/v2/track/${id}`, { status }, headers); setSavedMap(prev => ({ ...prev, [id]: { status } })); toast.success(`Status: ${status}`); } catch { toast.error('Failed'); } };

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="programs-arrival"
        eyebrow="Competitions & Programs"
        title="The line on your record that stands out."
        subtitle="Competitions, fellowships, summer programmes, hackathons and leadership tracks, the experiences that make a record memorable rather than adequate."
        accent="#fbbf24"
        second="#a78bfa"
        ctaLabel="Find a program"
        narration={"This is Competitions and Programs. Two applicants can have the same grades, and the one who is remembered did something. Competitions, fellowships, summer programmes, hackathons and leadership tracks all do that, and many pay, or cost nothing. Here you will see what exists for someone at your level, what each one asks for, when it opens, and what it is genuinely worth on a record."}
        facts={[
          { icon: A1, head: "Worth the effort", sub: "What each one actually adds to your record" },
          { icon: A3, head: "Timed right", sub: "When applications open, and how long to prepare" },
          { icon: A4, head: "Often funded", sub: "Many pay a stipend or cost nothing to enter" }
        ]}
        modules={[
          { icon: A1, label: "Competitions", line: "Contests where placing means something to employers", hue: "#fbbf24" },
          { icon: A4, label: "Fellowships", line: "Funded programmes with a name people recognise", hue: "#a78bfa" },
          { icon: A3, label: "Summer programmes", line: "Intensives that fit around school or work", hue: "#22d3ee" },
          { icon: A2, label: "Leadership tracks", line: "Programmes that build a record of running things", hue: "#34d399" },
          { icon: A5, label: "Search and filter", line: "By level, field, cost and deadline", hue: "#f472b6" },
          { icon: A6, label: "Add to your plan", line: "Anything you keep becomes a dated step", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader title="Programs & Competitions" description={stats ? `${stats.total_active} opportunities across the U.S., matched to your profile` : 'Discover competitions, pre-college programs, honor societies, and more'} />
      <div className="mb-4"><PatriciaPageGuide pageId="programs" /></div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total Programs', value: stats.total_active, icon: Award, gradient: 'from-blue-600/30 via-blue-500/10 to-transparent', border: 'border-blue-500/30', text: 'text-blue-300/70' },
            { label: 'Academic Competitions', value: stats.categories?.academic_competition || 0, icon: Trophy, gradient: 'from-amber-600/30 via-amber-500/10 to-transparent', border: 'border-amber-500/30', text: 'text-amber-300/70' },
            ctx.isCollege
              ? { label: 'Research / Fellowships', value: (stats.categories?.research_program || 0) + (stats.categories?.fellowship || 0), icon: Sparkles, gradient: 'from-purple-600/30 via-purple-500/10 to-transparent', border: 'border-purple-500/30', text: 'text-purple-300/70' }
              : { label: 'Pre-College', value: stats.categories?.pre_college || 0, icon: GraduationCap, gradient: 'from-purple-600/30 via-purple-500/10 to-transparent', border: 'border-purple-500/30', text: 'text-purple-300/70' },
            { label: 'Free Programs', value: stats.free_programs, icon: DollarSign, gradient: 'from-emerald-600/30 via-emerald-500/10 to-transparent', border: 'border-emerald-500/30', text: 'text-emerald-300/70' },
            { label: 'National / Virtual', value: `${stats.national_programs} / ${stats.virtual_programs}`, icon: Globe, gradient: 'from-cyan-600/30 via-cyan-500/10 to-transparent', border: 'border-cyan-500/30', text: 'text-cyan-300/70' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${s.gradient} border ${s.border} shadow-lg`}>
                <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-full -translate-y-4 translate-x-4" />
                <Icon className={`w-6 h-6 ${s.text.replace('/70', '')} mb-1`} />
                <p className="text-white font-bold text-2xl">{s.value}</p>
                <p className={`${s.text} text-xs font-medium`}>{s.label}</p>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      {/* Disclaimer (compact) */}
      <div className="mb-4 flex items-center gap-2" data-testid="programs-disclaimer-top">
        <AlertCircle className="w-3.5 h-3.5 text-amber-500/60 shrink-0" />
        <p className="text-amber-400/50 text-[10px]">Program details & deadlines may change. <span className="underline decoration-dotted cursor-help" title="Program details, deadlines, and costs may change. Many programs recur annually with updated dates. Always verify current details directly with the program provider.">Always verify with providers.</span></p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-800 pb-3">
        {[
          { key: 'search', label: 'Browse All', icon: Search },
          { key: 'recommended', label: 'For You', icon: Sparkles },
          { key: 'saved', label: `Saved (${Object.keys(savedMap).length})`, icon: Bookmark },
        ].map(tab => {
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-300 hover:text-white'}`}
            data-testid={`tab-${tab.key}`}><Icon className="w-4 h-4" /> {tab.label}</button>;
        })}
      </div>

      {/* Search */}
      {activeTab === 'search' && (
        <>
          <form onSubmit={(e) => { e.preventDefault(); fetchPrograms(1); }} className="mb-4">
            <GlassCard>
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 z-10" />
                  <Input placeholder="Search programs (e.g., robotics, debate, leadership...)" value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} className="input-dark pl-11" data-testid="program-search-input" />
                </div>
                <Button type="submit" disabled={loading} className="btn-primary whitespace-nowrap" data-testid="search-progs-btn">
                  {loading ? <LoadingSpinner size="sm" /> : 'Search'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-slate-700 text-slate-300 whitespace-nowrap">
                  <Filter className="w-4 h-4 mr-2" /> Filters <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>
              </div>
              <AnimatePresence>
                {showFilters && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-800">
                      <div><label className="text-slate-300 text-xs mb-1 block">Category</label>
                        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2">
                          {visibleCats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select></div>
                      <div><label className="text-slate-300 text-xs mb-1 block">State</label>
                        <select value={filterState} onChange={(e) => setFilterState(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2">
                          <option value="">All States + National</option>
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select></div>
                      <div><label className="text-slate-300 text-xs mb-1 block">Format</label>
                        <select value={filterLocType} onChange={(e) => setFilterLocType(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2">
                          <option value="">Any</option><option value="in_person">In-Person</option><option value="virtual">Virtual</option><option value="hybrid">Hybrid</option>
                        </select></div>
                      <div><label className="text-slate-300 text-xs mb-1 block">Interest</label>
                        <select value={filterInterest} onChange={(e) => setFilterInterest(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2">
                          {INTEREST_TAGS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select></div>
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                        <input type="checkbox" checked={filterFreeOnly} onChange={(e) => setFilterFreeOnly(e.target.checked)} className="rounded bg-slate-800 border-slate-600" /> Free Only
                      </label>
                      <div className="ml-auto flex gap-2">
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs rounded-lg p-1.5">
                          {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                        <Button type="button" size="sm" variant="ghost" className="text-slate-300 text-xs"
                          onClick={() => { setFilterCategory(''); setFilterState(''); setFilterLocType(''); setFilterInterest(''); setFilterFreeOnly(false); setFilterGpa(''); setSortBy('relevance'); }}>Clear</Button>
                        <Button type="submit" size="sm" className="btn-primary text-xs">Apply</Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              {/* Quick Category Pills */}
              <div className="flex flex-wrap gap-2 mt-3">
                {visibleCats.slice(1).map(c => {
                  const Icon = c.icon;
                  return <button key={c.key} onClick={() => { setFilterCategory(c.key); fetchPrograms(1); }}
                    className={`px-3 py-1 rounded-full text-xs flex items-center gap-1.5 transition-colors ${filterCategory === c.key ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'}`}>
                    <Icon className="w-3 h-3" /> {c.label}
                  </button>;
                })}
              </div>
            </GlassCard>
          </form>
          {loading ? <div className="flex justify-center py-16"><LoadingSpinner /></div> : (
            <>
              <p className="text-slate-300 text-sm mb-4">{totalResults} programs found</p>
              <div className="grid md:grid-cols-2 gap-4" data-testid="program-results">
                {programs.map(prog => <ProgramCard key={prog.id} prog={prog} savedMap={savedMap} onSave={handleSave} onUnsave={handleUnsave} onSelect={setSelectedProgram} />)}
              </div>
              {programs.length === 0 && <GlassCard className="text-center py-12"><Search className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="text-slate-300">No programs found. Try adjusting your filters.</p></GlassCard>}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <Button disabled={page <= 1} variant="outline" size="sm" onClick={() => fetchPrograms(page - 1)} className="border-slate-700 text-slate-300">Previous</Button>
                  <span className="text-slate-300 text-sm self-center">Page {page} of {totalPages}</span>
                  <Button disabled={page >= totalPages} variant="outline" size="sm" onClick={() => fetchPrograms(page + 1)} className="border-slate-700 text-slate-300">Next</Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'recommended' && (
        <>
          <div className="mb-4 p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl border border-purple-500/20">
            <p className="text-purple-300 text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" /> Programs ranked by how well they match your interests, grade level, and goals.</p>
          </div>
          {loading ? <div className="flex justify-center py-16"><LoadingSpinner /></div> : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="rec-results">
              {programs.map(prog => <ProgramCard key={prog.id} prog={prog} savedMap={savedMap} onSave={handleSave} onUnsave={handleUnsave} onSelect={setSelectedProgram} />)}
            </div>
          )}
        </>
      )}

      {activeTab === 'saved' && (
        savedList.length === 0 ? (
          <GlassCard className="text-center py-12">
            <Bookmark className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-white font-semibold mb-1">No saved programs yet</p>
            <p className="text-slate-300 text-sm mb-4">Browse and save programs to track your applications here.</p>
            <Button onClick={() => setActiveTab('search')} className="btn-primary"><Search className="w-4 h-4 mr-2" /> Browse Programs</Button>
          </GlassCard>
        ) : (
          <>
            <div className="flex gap-3 mb-4 flex-wrap">
              {TRACK_STATUSES.map(ts => {
                const count = savedList.filter(p => (p.tracking?.status || 'saved') === ts.key).length;
                return <div key={ts.key} className={`px-3 py-2 rounded-lg bg-${ts.color}-500/10 text-${ts.color}-400 text-xs font-medium`}>{ts.label}: {count}</div>;
              })}
            </div>
            <div className="grid md:grid-cols-2 gap-4" data-testid="saved-progs">
              {savedList.map(prog => <ProgramCard key={prog.id} prog={prog} savedMap={savedMap} onSave={handleSave} onUnsave={handleUnsave} onSelect={setSelectedProgram} />)}
            </div>
          </>
        )
      )}

      {/* Footer Disclaimer (compact) */}
      <div className="mt-6 flex items-center gap-2">
        <AlertCircle className="w-3 h-3 text-amber-500/40 shrink-0" />
        <p className="text-amber-400/40 text-[10px]">Program info may be outdated. <span className="underline decoration-dotted cursor-help" title="Program information may be outdated. Always confirm eligibility, deadlines, and costs directly on the program's official website.">Verify with providers.</span></p>
      </div>

      <AnimatePresence>
        {selectedProgram && <ProgramDetailModal prog={selectedProgram} savedMap={savedMap} onClose={() => setSelectedProgram(null)} onSave={handleSave} onUnsave={handleUnsave} onTrack={handleTrack} />}
      </AnimatePresence>
    </AppLayout>
  );
}
